import { useMemo, useState } from 'react'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { useNls } from '../../../nls/nlsProvider'
import { Pressable, TextInput } from '../../Common'

export type DrillTarget = string | null

export type ChartRecord = {
  id: string
  primary: string
  secondary?: string
  numeric?: number
  drillTarget?: DrillTarget
  meta?: Record<string, string | number>
}

export interface RecordListPanelProps<T extends ChartRecord = ChartRecord> {
  records: T[]
  loading: boolean
  onDrillRecord?: (record: T) => void
}

export const RecordListPanel = <T extends ChartRecord = ChartRecord>({
  records,
  loading,
  onDrillRecord,
}: RecordListPanelProps<T>) => {
  const { t } = useNls()
  const [query, setQuery] = useState('')
  const visibleRecords = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return records
    return records.filter((record) => `${record.primary} ${record.secondary ?? ''}`.toLowerCase().includes(normalized))
  }, [query, records])

  return (
    <section className="analysisChartRecords" id="analysis-chart-records" aria-label={t('NLSAnalysisChartLabelRecords', { count: records.length })}>
      <header>
        <strong>{t('NLSAnalysisChartLabelRecords', { count: records.length })}</strong>
        <TextInput
          type="search"
          value={query}
          onChange={setQuery}
          placeholder={t('NLSAnalysisChartRecordSearchPlaceholder')}
          aria-label={t('NLSAnalysisChartRecordSearchPlaceholder')}
          leadingIcon={<SearchRoundedIcon fontSize="small" />}
        />
      </header>
      <div className="analysisChartRecords__body" aria-live="polite">
        {loading ? (
          Array.from({ length: 5 }, (_, index) => <span className="analysisChartRecordSkeleton" key={index} />)
        ) : visibleRecords.length ? visibleRecords.map((record) => (
          <Pressable
            key={record.id}
            className="analysisChartRecordRow"
            disabled={!record.drillTarget && !onDrillRecord}
            onClick={() => onDrillRecord?.(record)}
          >
            <span>{record.secondary ?? 'Record'}</span>
            <strong>{record.primary}</strong>
            {record.numeric !== undefined && <em>{record.numeric}</em>}
            <ChevronRightRoundedIcon fontSize="small" />
          </Pressable>
        )) : (
          <p>{t('NLSAnalysisChartEmptyRecords')}</p>
        )}
      </div>
    </section>
  )
}
